import * as Yup from 'yup';

/**
 * Validation Schema for Accreditation Scores
 *
 * Core Areas (Required):
 * 1. Academic Content/Curriculum
 * 2. Staffing
 * 3. Physical Facilities
 * 4. Library
 *
 * Supplementary Areas (Optional):
 * 5. Funding
 * 6. Admission & Students' Welfare
 * 7. Institutional Linkages & Research
 */

// Individual score validation
const scoreValidation = Yup.number()
  .transform((value, originalValue) => {
    // Handle empty strings and null values
    if (originalValue === '' || originalValue === null || originalValue === undefined) {
      return undefined;
    }
    return value;
  })
  .min(0, 'Score must be at least 0')
  .max(100, 'Score must not exceed 100')
  .test('decimal-places', 'Score must have at most 2 decimal places', (value) => {
    if (value === undefined || value === null) return true;
    const decimalPlaces = (value.toString().split('.')[1] || '').length;
    return decimalPlaces <= 2;
  });

// Core areas validation (required)
const requiredScore = scoreValidation
  .required('This field is required')
  .typeError('Please enter a valid number');

// Supplementary areas validation (optional)
const optionalScore = scoreValidation
  .nullable()
  .notRequired()
  .typeError('Please enter a valid number');

/**
 * Main scores validation schema
 */
export const scoresSchema = Yup.object().shape({
  academicContent: requiredScore.label('Academic Content/Curriculum'),
  staffing: requiredScore.label('Staffing'),
  physicalFacilities: requiredScore.label('Physical Facilities'),
  library: requiredScore.label('Library'),
  funding: optionalScore.label('Funding'),
  admissionStudentWelfare: optionalScore.label('Admission & Students\' Welfare'),
  institutionalLinkages: optionalScore.label('Institutional Linkages & Research'),
});

/**
 * Field definitions with metadata
 */
export const scoreFields = [
  {
    key: 'academicContent',
    label: 'Academic Content/Curriculum',
    required: true,
    description: 'Assessment of curriculum design, learning outcomes, and academic standards',
    maxScore: 100,
  },
  {
    key: 'staffing',
    label: 'Staffing',
    required: true,
    description: 'Evaluation of teaching staff qualifications, experience, and adequacy',
    maxScore: 100,
  },
  {
    key: 'physicalFacilities',
    label: 'Physical Facilities',
    required: true,
    description: 'Assessment of classrooms, laboratories, and infrastructure',
    maxScore: 100,
  },
  {
    key: 'library',
    label: 'Library',
    required: true,
    description: 'Evaluation of library resources, facilities, and services',
    maxScore: 100,
  },
  {
    key: 'funding',
    label: 'Funding',
    required: false,
    description: 'Assessment of financial resources and budget allocation',
    maxScore: 100,
  },
  {
    key: 'admissionStudentWelfare',
    label: 'Admission & Students\' Welfare',
    required: false,
    description: 'Evaluation of admission processes and student support services',
    maxScore: 100,
  },
  {
    key: 'institutionalLinkages',
    label: 'Institutional Linkages & Research',
    required: false,
    description: 'Assessment of partnerships, collaborations, and research activities',
    maxScore: 100,
  },
];

/**
 * Validate scores object against schema
 * @param {Object} scores - Scores object to validate
 * @returns {Promise<{isValid: boolean, errors: Object}>}
 */
export const validateScores = async (scores) => {
  try {
    await scoresSchema.validate(scores, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return { isValid: false, errors };
    }
    throw error;
  }
};

/**
 * Validate a single field
 * @param {string} fieldName - Name of the field to validate
 * @param {any} value - Value to validate
 * @returns {Promise<{isValid: boolean, error: string|null}>}
 */
export const validateField = async (fieldName, value) => {
  try {
    await scoresSchema.validateAt(fieldName, { [fieldName]: value });
    return { isValid: true, error: null };
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      return { isValid: false, error: error.message };
    }
    throw error;
  }
};

/**
 * Get required fields
 * @returns {string[]} Array of required field names
 */
export const getRequiredFields = () => {
  return scoreFields.filter(field => field.required).map(field => field.key);
};

/**
 * Get optional fields
 * @returns {string[]} Array of optional field names
 */
export const getOptionalFields = () => {
  return scoreFields.filter(field => !field.required).map(field => field.key);
};

/**
 * Check if all required fields are filled
 * @param {Object} scores - Scores object
 * @returns {boolean}
 */
export const areRequiredFieldsFilled = (scores) => {
  const requiredFields = getRequiredFields();
  return requiredFields.every(field => {
    const value = scores[field];
    return value !== null && value !== undefined && value !== '';
  });
};

/**
 * Get field metadata
 * @param {string} fieldName - Name of the field
 * @returns {Object|null} Field metadata or null if not found
 */
export const getFieldMetadata = (fieldName) => {
  return scoreFields.find(field => field.key === fieldName) || null;
};

export default scoresSchema;